/*
  Simple, configurable recommendations engine.
  Inputs: systemStats, analyticsData (from AdvancedAnalyticsPanel), homeStats (derived), options/thresholds.
  Output: array of { category, insight, recommendation, severity, metricRefs }
*/

export type Severity = 'low' | 'medium' | 'high';

export interface Recommendation {
  category: string;
  insight: string;
  recommendation: string;
  severity: Severity;
  metricRefs?: Record<string, number | string>;
}

export interface RecommendationThresholds {
  engagementRateLow: number; // %
  conversionRateLow: number; // %
  adCtrLow: number; // %
  searchShareLow: number; // % of other traffic assumed as search
  mobileSkewHigh: number; // %
  socialDominanceHigh: number; // %
}

export const defaultThresholds: RecommendationThresholds = {
  engagementRateLow: 3.0,
  conversionRateLow: 3.0,
  adCtrLow: 1.5,
  searchShareLow: 15,
  mobileSkewHigh: 70,
  socialDominanceHigh: 40,
};

export function generateRecommendations(args: {
  systemStats: any;
  analyticsData: any; // from AdvancedAnalyticsPanel useMemo
  homeStats: any; // from AdvancedAnalyticsPanel useMemo
  thresholds?: Partial<RecommendationThresholds>;
}): Recommendation[] {
  const { systemStats, analyticsData, homeStats } = args;
  const th: RecommendationThresholds = { ...defaultThresholds, ...(args.thresholds || {}) };
  const recs: Recommendation[] = [];

  const totalViews = Number(analyticsData?.userEngagement?.totalViews || systemStats?.totalViews || 0);
  const totalClicks = Number(analyticsData?.userEngagement?.totalClicks || 0);
  const engagementRate = totalViews > 0 ? (totalClicks / totalViews) * 100 : 0;
  const conversionRate = ((Number(systemStats?.silverSubscriptions || 0) + Number(systemStats?.goldSubscriptions || 0)) / Math.max(1, Number(systemStats?.totalUsers || 0))) * 100;
  const newUsersGrowthRate = Number(analyticsData?.newUsersStats?.growthRate || 0);
  const newUsersTotal = Number(analyticsData?.newUsersStats?.totalNewUsers || 0);
  const homeViews = Number(homeStats?.totalViews || 0);
  const silver = Number(systemStats?.silverSubscriptions || 0);
  const gold = Number(systemStats?.goldSubscriptions || 0);
  const _trial = Number(systemStats?.trialUsers || 0);
  const totalUsers = Number(systemStats?.totalUsers || 0);
  const activeSubscribers = silver + gold;
  const inactiveSubscribers = Math.max(0, totalUsers - activeSubscribers);

  // 1) Engagement hygiene
  if (engagementRate < th.engagementRateLow && totalViews > 1000) {
    recs.push({
      category: 'Engagement Optimization',
      insight: `Engagement rate is ${engagementRate.toFixed(1)}% with ${totalViews.toLocaleString()} views`,
      recommendation: 'Improve onboarding and above-the-fold CTAs; A/B test hero section and benefit bullets.',
      severity: 'high',
      metricRefs: { engagementRate: engagementRate.toFixed(1), totalViews }
    });
  }

  // 2) Conversion funnel
  if (conversionRate < th.conversionRateLow && (homeStats?.totalViews || 0) > 300) {
    recs.push({
      category: 'Conversion Funnel',
      insight: `Conversion rate is ${conversionRate.toFixed(1)}% while homepage views are ${Number(homeStats?.totalViews || 0).toLocaleString()}`,
      recommendation: 'Simplify pricing and checkout; add trust signals (testimonials, guarantees); reduce form friction.',
      severity: 'medium',
      metricRefs: { conversionRate: conversionRate.toFixed(1), homepageViews: homeStats?.totalViews || 0 }
    });
  }

  // 3) Channel focus (Social dominance)
  const socialEntries = Array.isArray(homeStats?.socialEntries) ? homeStats.socialEntries : [];
  const topSocial = socialEntries[0];
  if (topSocial) {
    const shareNum = parseFloat(String(topSocial.percentage).replace('%','')) || 0;
    if (shareNum >= th.socialDominanceHigh) {
      recs.push({
        category: 'Channel Focus',
        insight: `${topSocial.label} contributes ${topSocial.percentage} of social traffic`,
        recommendation: `Double down on ${topSocial.label}: tailor creatives and increase cadence; replicate winning patterns on adjacent channels.`,
        severity: 'medium',
        metricRefs: { channel: topSocial.label, share: topSocial.percentage }
      });
    }
  }

  // 4) SEO gap (low search share)
  const otherEntries = Array.isArray(homeStats?.otherEntries) ? homeStats.otherEntries : [];
  const searchRow = otherEntries.find((e: any) => /search/i.test(String(e.label)));
  const searchPct = searchRow ? (parseFloat(String(searchRow.percentage).replace('%','')) || 0) : 0;
  const directRow = otherEntries.find((e: any) => /direct/i.test(String(e.label)));
  const directPct = directRow ? (parseFloat(String(directRow.percentage).replace('%','')) || 0) : 0;
  if (searchPct < th.searchShareLow && directPct > 35) {
    recs.push({
      category: 'SEO Foundation',
      insight: `Search share ${searchPct.toFixed(1)}% appears low vs direct ${directPct.toFixed(1)}%`,
      recommendation: 'Ship robots.txt, sitemap.xml, canonical URLs, and JSON-LD; publish 3–5 targeted landing pages.',
      severity: 'medium',
      metricRefs: { searchPct: searchPct.toFixed(1), directPct: directPct.toFixed(1) }
    });
  }

  // 5) Mobile-first skew
  const deviceRow = (homeStats?.deviceEntries || []).find((e: any) => /mobile/i.test(String(e.label)));
  const mobilePct = deviceRow ? (parseFloat(String(deviceRow.percentage).replace('%','')) || 0) : 0;
  if (mobilePct > th.mobileSkewHigh) {
    recs.push({
      category: 'Mobile Experience',
      insight: `Mobile accounts for ${mobilePct.toFixed(1)}% of traffic`,
      recommendation: 'Prioritize mobile performance and UX: image optimization, font loading, tap targets, sticky CTA.',
      severity: 'low',
      metricRefs: { mobilePct: mobilePct.toFixed(1) }
    });
  }

  // 6) Ads tuning
  const adViews = Number(analyticsData?.userEngagement?.totalViews || 0) * 0.15; // derived baseline when server is missing exact ad views
  const adClicks = Number(analyticsData?.userEngagement?.totalClicks || 0) * 0.08;
  const adCtr = adViews > 0 ? (adClicks / adViews) * 100 : 0;
  if (adCtr < th.adCtrLow && adViews > 500) {
    recs.push({
      category: 'Ads Optimization',
      insight: `Estimated ad CTR ${adCtr.toFixed(2)}% from ${Math.floor(adViews)} views`,
      recommendation: 'A/B test creatives and placements; refine targeting; rotate underperforming ads.',
      severity: 'low',
      metricRefs: { adCtr: adCtr.toFixed(2), adViews: Math.floor(adViews) }
    });
  }

  // --- Explicit categories requested by user ---
  // 7) Advertisement Strategy
  recs.push({
    category: 'Advertisement Strategy',
    insight: `Ad CTR is ${adCtr.toFixed(2)}% with ~${Math.floor(adClicks).toLocaleString()} clicks from ~${Math.floor(adViews).toLocaleString()} views`,
    recommendation: adCtr < th.adCtrLow
      ? 'Improve ad performance: test new creatives, narrow targeting, and adjust placements for higher visibility.'
      : 'Maintain current winning creatives; gradually scale budget while monitoring CTR and CPA.',
    severity: adCtr < th.adCtrLow ? 'medium' : 'low',
    metricRefs: { adCtr: adCtr.toFixed(2) }
  });

  // 8) Revenue Growth
  const estMonthlyRevenue = (silver * 9.99) + (gold * 19.99);
  recs.push({
    category: 'Revenue Growth',
    insight: `Conversion rate is ${conversionRate.toFixed(1)}%; est. MRR ~$${Math.round(estMonthlyRevenue).toLocaleString()}`,
    recommendation: conversionRate < th.conversionRateLow
      ? 'Raise conversion by refining pricing page, adding social proof, and simplifying checkout; test annual plan incentives.'
      : 'Explore upsell paths (add-ons, team plans) and lifecycle emails to increase ARPU.',
    severity: conversionRate < th.conversionRateLow ? 'medium' : 'low',
    metricRefs: { conversionRate: conversionRate.toFixed(1), mrr: Math.round(estMonthlyRevenue) }
  });

  // 9) User Acquisition
  recs.push({
    category: 'User Acquisition',
    insight: `Monthly growth trend indicates ${newUsersGrowthRate >= 0 ? '+' : ''}${newUsersGrowthRate.toFixed(1)}% with ${newUsersTotal.toLocaleString()} new users this period`,
    recommendation: newUsersGrowthRate < 5
      ? 'Boost acquisition: launch referral incentives, ship 2–3 SEO pages, and run targeted social campaigns.'
      : 'Sustain growth by doubling down on top channels and expanding high-performing segments.',
    severity: newUsersGrowthRate < 0 ? 'high' : newUsersGrowthRate < 5 ? 'medium' : 'low',
    metricRefs: { growthRate: newUsersGrowthRate.toFixed(1), newUsers: newUsersTotal }
  });

  // 10) User growth rate (explicit title)
  recs.push({
    category: 'User growth rate',
    insight: `Growth rate is ${newUsersGrowthRate >= 0 ? '+' : ''}${newUsersGrowthRate.toFixed(1)}% vs previous period`,
    recommendation: newUsersGrowthRate <= 0
      ? 'Reverse decline: audit funnel drop-offs, refresh creatives, and trial a new offer for 14 days.'
      : 'Monitor cohort retention; invest in the channels driving the growth without inflating CAC.',
    severity: newUsersGrowthRate <= 0 ? 'high' : newUsersGrowthRate < 5 ? 'medium' : 'low',
  });

  // 11) home page site visits
  recs.push({
    category: 'home page site visits',
    insight: `Home page recorded ${homeViews.toLocaleString()} visits in the selected period`,
    recommendation: homeViews < 1000
      ? 'Increase top-of-funnel traffic: publish shareable content, cross-post on social, and ensure clear CTAs above the fold.'
      : 'Leverage high traffic: test hero copy and CTA placement to lift click-through to key actions.',
    severity: homeViews < 500 ? 'medium' : 'low',
    metricRefs: { homeViews }
  });

  // 12) No of sign up users
  recs.push({
    category: 'No of sign up users',
    insight: `Recorded ${newUsersTotal.toLocaleString()} new sign-ups for the period`,
    recommendation: newUsersTotal < 100
      ? 'Grow sign-ups via time-bound promos, clearer value props, and streamlined registration UX.'
      : 'Nurture sign-ups with onboarding emails, in-app tours, and early activation nudges.',
    severity: newUsersTotal < 50 ? 'medium' : 'low',
    metricRefs: { signUps: newUsersTotal }
  });

  // 13) Active Vs Inactive Subscribers
  const activeShare = totalUsers > 0 ? (activeSubscribers / totalUsers) * 100 : 0;
  recs.push({
    category: 'Active Vs Inactive Subscribers',
    insight: `Active subscribers: ${activeSubscribers.toLocaleString()} (${activeShare.toFixed(1)}%) | Inactive: ${inactiveSubscribers.toLocaleString()}`,
    recommendation: activeShare < 25
      ? 'Re-engage inactive users: targeted win-back emails, in-app prompts, and a limited-time discount.'
      : 'Introduce loyalty perks and referral rewards to maintain activity and drive advocacy.',
    severity: activeShare < 15 ? 'high' : activeShare < 30 ? 'medium' : 'low',
    metricRefs: { activeSubscribers, inactiveSubscribers, activeShare: activeShare.toFixed(1) }
  });

  // 14) Social Media Traffic
  const socialEntries2 = Array.isArray(homeStats?.socialEntries) ? homeStats.socialEntries : [];
  const topSocial2 = socialEntries2[0];
  const topSocialShare = topSocial2 ? (parseFloat(String(topSocial2.percentage).replace('%','')) || 0) : 0;
  recs.push({
    category: 'Social Media Traffic',
    insight: topSocial2
      ? `${topSocial2.label} leads social with ${topSocial2.percentage} share`
      : 'Social traffic distribution data not available; using defaults',
    recommendation: topSocialShare < 30
      ? 'Increase social share: post platform-native content 3–5x/week, collaborate with micro-influencers, and use UTM tracking.'
      : `Double down on ${topSocial2?.label || 'top social'} while testing 1–2 adjacent platforms for incremental reach.`,
    severity: topSocialShare < 20 ? 'medium' : 'low',
    metricRefs: topSocial2 ? { channel: topSocial2.label, share: topSocial2.percentage } : undefined
  });

  // 15) Other Platfrom Traffic (spelling preserved per request)
  const otherEntries2 = Array.isArray(homeStats?.otherEntries) ? homeStats.otherEntries : [];
  const searchRow2 = otherEntries2.find((e: any) => /search/i.test(String(e.label)));
  const directRow2 = otherEntries2.find((e: any) => /direct/i.test(String(e.label)));
  const referralRow2 = otherEntries2.find((e: any) => /referral/i.test(String(e.label)));
  const emailRow2 = otherEntries2.find((e: any) => /email/i.test(String(e.label)));
  const searchPct2 = searchRow2 ? (parseFloat(String(searchRow2.percentage).replace('%','')) || 0) : 0;
  const directPct2 = directRow2 ? (parseFloat(String(directRow2.percentage).replace('%','')) || 0) : 0;
  recs.push({
    category: 'Other Platfrom Traffic',
    insight: `Direct ${directPct2.toFixed(1)}% | Search ${searchPct2.toFixed(1)}% | Referral ${referralRow2?.percentage || '—'} | Email ${emailRow2?.percentage || '—'}`,
    recommendation: searchPct2 < th.searchShareLow && directPct2 > 35
      ? 'Strengthen SEO: add sitemap, canonical, JSON-LD; publish landing pages and optimize titles/meta.'
      : 'Sustain balanced mix; keep refining referral partnerships and email cadence.',
    severity: searchPct2 < th.searchShareLow && directPct2 > 35 ? 'medium' : 'low',
    metricRefs: { searchPct: searchPct2.toFixed(1), directPct: directPct2.toFixed(1) }
  });

  // Ensure at least one recommendation is present for executive guidance
  if (recs.length === 0) {
    recs.push({
      category: 'Growth Opportunities',
      insight: 'Core metrics are stable; no critical issues detected.',
      recommendation: 'Run small experiments: signup copy tests, pricing page variant, and a weekly content cadence.',
      severity: 'low'
    });
  }

  // --- Multi-window & retention driven recommendations (new) ---
  try {
    const uv7 = Number(systemStats?.uniqueVisitors7d || 0);
    const uv30 = Number(systemStats?.uniqueVisitors30d || 0);
    const ret30 = Number(systemStats?.returningVisitors30d || 0);
    const avgSess = Number(systemStats?.averageSessionTime || 0); // seconds weighted
    if (uv30 > 0) {
      const retentionRate = (ret30 / uv30) * 100;
      if (retentionRate < 25 && uv30 >= 100) {
        recs.push({
          category: 'User Retention',
          insight: `30d retention rate is ${retentionRate.toFixed(1)}% (${ret30}/${uv30} returning)`,
          recommendation: 'Introduce activation & re-engagement loops: onboarding email series, post-signup checklist, and win-back campaigns.',
          severity: retentionRate < 15 ? 'high' : 'medium',
          metricRefs: { retentionRate: retentionRate.toFixed(1), returningVisitors30d: ret30, uniqueVisitors30d: uv30 }
        });
      }
      const acquisitionMomentum = uv7 / Math.max(1, uv30);
      if (acquisitionMomentum < 0.18 && uv30 >= 150) {
        recs.push({
          category: 'Top-of-Funnel Acquisition',
          insight: `Recent 7d uniques are ${(uv7).toLocaleString()} (${(acquisitionMomentum*100).toFixed(1)}% of 30d)`,
          recommendation: 'Scale awareness: launch referral incentive, publish 2 SEO landing pages, and test a lightweight paid campaign.',
          severity: acquisitionMomentum < 0.12 ? 'medium' : 'low',
          metricRefs: { uniqueVisitors7d: uv7, uniqueVisitors30d: uv30, momentumPct: (acquisitionMomentum*100).toFixed(1) }
        });
      }
    }
    if (avgSess > 0) {
      // Convert weighted seconds to minutes for evaluation
      const avgSessMin = avgSess / 60;
      if (avgSessMin < 2 && uv30 > 200) {
        recs.push({
          category: 'Session Depth',
          insight: `Avg weighted session ≈ ${avgSessMin.toFixed(1)} mins (<2m baseline)`,
          recommendation: 'Enhance in-session value: surface contextual CTAs, add quick actions, and improve internal navigation speed.',
          severity: avgSessMin < 1.2 ? 'medium' : 'low',
          metricRefs: { averageSessionTimeSecs: avgSess, averageSessionTimeMins: avgSessMin.toFixed(1) }
        });
      }
    }
  } catch {}

  return recs;
}
